import type {
  CardFieldElementType,
  PaypercutElementsAppearance,
  ElementsErrorEvent,
  PaypercutPaymentMethodFutureUsage,
} from '../types/elements';

export type CardCollectionFieldStyle = Record<string, unknown>;

export type CardCollectionProviderFieldValue = string | Readonly<{ month: number; year: number }>;

// Version 1 is retained so new SDKs can initialize against an older Hosted Checkout.
export const LEGACY_CARD_COLLECTION_PROTOCOL_VERSION = 1 as const;

// Version 2 requires Hosted Checkout-delivered locale copy before provider mount.
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const LOCALIZED_CARD_COLLECTION_PROTOCOL_VERSION = 2 as const;

// Version 3 adds the private tokenization/submission capability while retaining
// the version-2 render contract.
// eslint-disable-next-line @typescript-eslint/no-magic-numbers
export const CARD_COLLECTION_PROTOCOL_VERSION = 3 as const;

export type CardCollectionMessages = {
  card: {
    cardNumber: string;
    cardNumberPlaceholder: string;
    expirationDate: string;
    expirationDatePlaceholder: string;
    securityCode: string;
    cvvPlaceholder: string;
    errors: {
      number: { incomplete: string; invalid: string };
      expirationDate: { incomplete: string; invalid: string };
      cvv: { incomplete: string; invalid: string };
    };
  };
};

type CardCollectionBootstrapBase = {
  driver: 'basis_theory_v2';
  publicKey: string;
  livemode: boolean;
  expiresAt: string;
};

export type LegacyCardCollectionBootstrap = CardCollectionBootstrapBase & {
  protocolVersion: typeof LEGACY_CARD_COLLECTION_PROTOCOL_VERSION;
};

type LocalizedCardCollectionBootstrapBase = CardCollectionBootstrapBase & {
  locale: string;
  messages: CardCollectionMessages;
};

export type LocalizedCardCollectionBootstrap = LocalizedCardCollectionBootstrapBase & {
  protocolVersion: typeof LOCALIZED_CARD_COLLECTION_PROTOCOL_VERSION;
};

export type AvailableCardCollectionBootstrap = LocalizedCardCollectionBootstrapBase & {
  protocolVersion: typeof CARD_COLLECTION_PROTOCOL_VERSION;
  capabilities: { paymentMethodSubmission: true };
};

export type UnavailableCardCollectionBootstrap = CardCollectionBootstrapBase & {
  protocolVersion:
    | typeof LOCALIZED_CARD_COLLECTION_PROTOCOL_VERSION
    | typeof CARD_COLLECTION_PROTOCOL_VERSION;
  error: ElementsErrorEvent;
};

export type CardCollectionBootstrap =
  | LegacyCardCollectionBootstrap
  | LocalizedCardCollectionBootstrap
  | AvailableCardCollectionBootstrap
  | UnavailableCardCollectionBootstrap;

export type RenderableCardCollectionBootstrap =
  | LocalizedCardCollectionBootstrap
  | AvailableCardCollectionBootstrap;

export type CardCollectionConfirmationData = {
  instrumentToken: { type: 'token' | 'token_intent'; id: string };
  authentication?: 'sca_required' | 'sca_not_required';
  card: {
    brand?: string;
    display_brand?: string;
    last4?: string;
    exp_month?: number;
    exp_year?: number;
    fingerprint?: string;
    iin?: string;
    funding?: string;
    issuer?: string;
    country?: string;
    segment?: string;
    cardholder_name?: string;
  };
};

export type CardCollectionFieldErrorType = 'incomplete' | 'invalid';

export type CardCollectionFieldChange = {
  complete: boolean;
  empty: boolean;
  brand?: string;
  errorType?: CardCollectionFieldErrorType;
};

export type CardCollectionFieldPresentation = {
  floatingLabels: boolean;
  floatingLabelActive: boolean;
  placeholder: string;
};

export type CardCollectionFieldEvents = {
  onReady(): void;
  onChange(change: CardCollectionFieldChange): void;
  onFocus(): void;
  onBlur(): void;
  onError(error: ElementsErrorEvent): void;
};

export type CardCollectionFieldOptions = {
  type: CardFieldElementType;
  target: HTMLElement;
  /** Aborted when the owning field or Elements group is torn down. */
  signal: AbortSignal;
  style: CardCollectionFieldStyle;
  ariaLabel: string;
  placeholder: string;
  events: CardCollectionFieldEvents;
};

export interface CardCollectionProviderField {
  clear(): void;
  focus(): void;
  blur(): void;
  update(options: {
    style?: CardCollectionFieldStyle;
    cardBrand?: string;
    placeholder?: string;
    value?: CardCollectionProviderFieldValue;
  }): Promise<void>;
  destroy(): void;
}

export interface CardCollectionProvider {
  createField(options: CardCollectionFieldOptions): Promise<CardCollectionProviderField>;
  createConfirmationData(
    requestId: string,
    cardholderName?: string,
    futureUsage?: PaypercutPaymentMethodFutureUsage,
  ): Promise<CardCollectionConfirmationData>;
}

export type CardCollectionProviderContext = {
  /** Aborted when the owning Elements group is destroyed. */
  signal: AbortSignal;
};

export type CardCollectionProviderFactory = (
  bootstrap: RenderableCardCollectionBootstrap,
  context: CardCollectionProviderContext,
) => Promise<CardCollectionProvider>;

export type CardFieldAppearanceCompiler = (
  appearance: PaypercutElementsAppearance | undefined,
  presentation?: CardCollectionFieldPresentation,
) => CardCollectionFieldStyle;
